import React, { useRef, useEffect, useState } from "react";

interface Tab {
  label: string;
  content: React.ReactNode;
}

interface LineTabsProps {
  tabs: Tab[];
  activeTab?: number;
  onTabChange: (index: number) => void;
  borderColor?: string;
  activeColor?: string;
  className?: string;
  position?: string;
}

const Tabs: React.FC<LineTabsProps> = ({
  tabs = [],
  activeTab = 0,
  onTabChange,
  borderColor = "border-gray-200",
  activeColor = "border-blue-500",
  className = "",
  position,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [tabs]);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollLeft +=
        direction === "left" ? -scrollAmount : scrollAmount;
    }
  };

  return (
    <div className={`${className}`}>
      <div className="relative">
        <div
          ref={scrollContainerRef}
          className={`
            flex overflow-x-auto scrollbar-hide
            ${position === "center" ? "justify-center" : ""}
            border-b ${borderColor}
          `}
        >
          <div className="flex gap-0 md:gap-2 items-center min-w-min font-sans">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => onTabChange(index)}
                className={`
                  relative whitespace-nowrap px-4 h-[60.85px] -mb-px text-sm font-medium
                  transition-colors ease-in-out delay-200
                  hover:text-bnp-cyan focus:outline-none
                  ${
                    activeTab === index
                      ? `text-bnp-cyan font-bold border-b-2 ${activeColor}`
                      : "text-grey-7"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 px-2 lg:px-0">{tabs[activeTab]?.content}</div>
    </div>
  );
};

export default Tabs;
